<script lang="ts">
	import { onMount } from 'svelte';
	import { resolve } from '$app/paths';
	import Popup from '$components/Popup.svelte';
	import defaultUserThumbnail from '$img/thumbnails/default-user.jpg';
	import { fetchUser } from '$lib/features/auth/api/auth';
	import type { User } from '$lib/shared/lib/types';

	interface Props {
		classes?: string;
		user?: User | null;
		children?: import('svelte').Snippet<[User]>;
	}

	let { classes = '', user = null, children }: Props = $props();

	// `undefined` while auth is resolving, `User` when signed in, `null` when not.
	// Prerendered pages bake in a null user, so re-validate on mount and show a
	// placeholder until it settles instead of flashing the sign-in button.
	let resolved = $derived<User | null | undefined>(user ?? undefined);

	onMount(async () => {
		if (resolved !== undefined) return;
		resolved = await fetchUser(fetch);
	});

	let triggerEl: HTMLDivElement = $state()!;
	let popupOpen = $state(false);

	function handleClickOutside(e: MouseEvent) {
		if (!triggerEl) return;
		const target = e.target as Node;
		if (triggerEl.contains(target)) return;
		popupOpen = false;
	}
</script>

<svelte:window onclick={handleClickOutside} />

{#if resolved === undefined}
	<div class="placeholder-circle h-8 w-8 animate-pulse sm:h-10 sm:w-10 {classes}"></div>
{:else if resolved}
	<div
		bind:this={triggerEl}
		role="button"
		tabindex="0"
		onclick={() => (popupOpen = !popupOpen)}
		onkeydown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				popupOpen = !popupOpen;
			}
		}}
		class=""
	>
		<img
			src={resolved.avatar ?? defaultUserThumbnail}
			alt="avatar"
			class="h-8 w-8 rounded-full border-2 border-surface-50 object-cover hover:cursor-pointer hover:border-primary-500! active:border-primary-200! sm:h-10 sm:w-10 {classes}"
		/>
	</div>

	<Popup bind:open={popupOpen} {triggerEl} placement="bottom">
		{@render children?.(resolved)}
	</Popup>
{:else}
	<button class="rounded-full bg-primary-500 btn-sm py-1 whitespace-nowrap text-white">
		<a class="hidden p-1 xl:block xl:text-base" href={resolve('/signin')}>Sign in/Sign up</a>
		<a class="text-sm md:text-base xl:hidden" href={resolve('/signin')}>Sign Up</a>
	</button>
{/if}
